import { clientApi } from '@phoenix/clientApi';
import Bag from '@phoenix/components/Bag';
import {
  filter,
  find,
  forEach,
  get,
  has,
  head,
  isEmpty,
  isNil,
  isUndefined,
  keys,
  set,
  size,
  some,
  sortBy,
  uniq,
} from 'lodash-es';
import * as types from './mutations-types';

const ERROR_STOCK = 'ERROR_STOCK';
const ERROR_RESTRICTED_MATERIAL = 'ERROR_RESTRICTED_MATERIAL';

const initialState = {
  cart: {
    items: [],
    taxIncluded: false,
  },
  addresses: {},
  isLoading: false,
  currentCartItemId: null,
  error: null,
  summaryLoading: false,
  deliveryLoading: false,
  billingLoading: false,
  paymentLoading: false,
  checkoutAddressFormModalId: 'phoenix-checkout-address-form-modal',
  boutiques: [],
  selectedDeliveryAddressId: null,
  selectedBoutiqueId: null,
  addressType: null,
  editingAddressId: null,
  threeDSecure: {},
  threeDSecure2IdentifyShopper: {},
  threeDSecure2ChallengeShopper: {},
  groups: {},
  countries: [],
  fapiaoType: null,
  mountedSteps: [],
  guestEmailAddress: null,
};

const getters = {
  getItemById: (state) => (id) => find(state.cart.items, (item) => item.id === id),

  giftMessage: (state) => (state.cart ? state.cart.gift : null),

  hasGiftMessage: (state) => !isUndefined(get(state, ['cart', 'gift', 'message'], undefined)),

  hasGiftWrapping: (state) => get(state, ['cart', 'gift', 'hasWrapping'], false),

  countryCode: (state) => (state.cart ? state.cart.country_code : null),

  allProductsHaveStock: (state) => !some(get(state.cart, ['items'], []), (item) => item.errors.includes(ERROR_STOCK)),

  isDeliverable: (state) =>
    !!state.cart.isDeliverable ||
    !some(get(state.cart, ['items'], []), (item) => item.errors.includes(ERROR_RESTRICTED_MATERIAL)),

  getProductErrors: (state) =>
    uniq(get(state.cart, ['items'], []).reduce((carry, item) => carry.concat(item.errors), [])),

  hasBillingAddress: (state) => state.cart.order && size(state.cart.order.billing_address) > 0,

  hasDeliveryAddress: (state) => state.cart.order && size(state.cart.order.delivery_address) > 0,

  paymentTypes: (state) => (state.cart ? state.cart.payment_types : {}),

  invoiceSent: (state) => (state.cart ? state.cart.invoice_sent : false),

  canHavePaperInvoice: (state) => (state.cart ? state.cart.can_have_paper_invoice : false),

  stepByName: (state) => (name) => get(state.cart, ['steps', name], 0),

  shouldDisplayDelivery: (state, getters) => state.cart.step >= getters.stepByName('DELIVERY'),

  shouldDisplayDeliveryMethods: (state, getters) =>
    getters.shouldDisplayDelivery &&
    !isEmpty(state.cart.delivery_types) &&
    (!isNil(get(state.cart, ['order', 'selected_delivery_address_id'])) ||
      (get(state.cart, ['delivery_mode']) === 'pickup' && !isNil(get(state.cart, ['delivery_boutique_code'])))),

  shouldDisplayBoutiqueDeliveryMethods: (state, getters) =>
    getters.shouldDisplayDelivery &&
    state.selectedBoutiqueId !== null &&
    state.cart.boutique_delivery_types?.length > 1,

  shouldDisplayBilling: (state, getters) => state.cart.step >= getters.stepByName('BILLING') && !state.deliveryLoading,

  shouldDisplayPayment: (state, getters) =>
    state.cart.step >= getters.stepByName('PAYMENT') &&
    !state.billingLoading &&
    !getters.shouldHidePaymentBecauseOfFapiao,

  shouldDisplayAdyenPayment: (state, getters) =>
    getters.shouldDisplayPayment && !isNil(get(state.cart, ['adyen_payment'])),

  contactInformation: (state) => get(state.cart, ['contact_information', 'values']),

  shouldDisplayFapiao: (state) => get(state.cart, ['fapiao_activated']),

  shouldSaveFapiaoInformation: (state, getters) => getters.shouldDisplayFapiao && state.cart.fapiao_type === 'eFapiao',

  shouldHidePaymentBecauseOfFapiao: (state, getters) =>
    getters.shouldDisplayFapiao && state.cart.fapiao_type === 'VATRefundableFapiao',

  shouldDisplayProofOfIdentity: (state, getters) =>
    getters.shouldDisplayFiscalCode || getters.shouldDisplayPassportNumber,

  shouldDisplayFiscalCode: (state) => {
    const { type, status } = state.cart.proofOfIdentity;
    return type === 'FISCALCODE' && ['MANDATORY', 'OPTIONAL'].includes(status);
  },

  shouldDisplayPassportNumber: (state) => {
    const { type, status } = state.cart.proofOfIdentity;
    return type === 'PASSPORTNUMBER' && status === 'MANDATORY';
  },

  canGoToNextStep: (state) => get(state.cart, ['can_go_to_next_step'], false),

  countriesForAddressType: (state) => (addressType) => {
    if (addressType === null) {
      return [];
    }

    const countries =
      addressType !== 'delivery'
        ? state.countries
        : filter(state.countries, (country) => country.only_delivery === true);

    return sortBy(countries, ['label']);
  },

  selectedAddressId: (state) => (type) => get(state.cart, ['order', `selected_${type}_address_id`], null),

  selectedDeliveryAddressId: (state, getters) => getters.selectedAddressId('delivery'),

  selectedBillingAddressId: (state, getters) => getters.selectedAddressId('billing'),

  hasInlineAddressForm: (state) => get(state.cart, ['hasInlineAddressForm'], false),

  allStepsAreMounted: (state) => state.mountedSteps.length >= 4,

  tracking: (state) => get(state.cart, ['tracking'], {}),

  allowEmptyGiftMessage: (state) => get(state.cart, ['allowEmptyGiftMessage'], false),

  deliveryAddresses: (state, getters, rootState) => {
    let addresses = state.addresses;
    if (!rootState.currentUser.isLogged) {
      addresses = getters.hasDeliveryAddress ? [state.cart.order.delivery_address] : [];
    }

    return filter(addresses, (address) => address.is_delivery === true && !has(address, 'deliveryBoutiqueCode'));
  },

  step: (state) => get(state.cart, ['step'], null),

  guestEmailAddress: (state) => state.guestEmailAddress,
};

const actions = {
  async fetchCart({ commit, dispatch }, displayLoader = true) {
    try {
      if (displayLoader) {
        commit(types.CHECKOUT_SET_LOADING, true);
        // As it is the first time we get the cart, make sure to get the customer info too
        await dispatch('currentUser/loadCustomerInfo', null, {
          root: true,
        });
      }

      const { data } = await clientApi.get(window.route('api.cart.index'));

      if (!data.status) {
        return dispatch('showDefaultError');
      }

      dispatch('cartLoaded', data.data.cart);
    } catch (error) {
      commit(types.CHECKOUT_ERROR, error);
    }
  },

  async fetchAddresses({ state, commit, dispatch, rootState, getters }) {
    // We need to make sure that the user logging status is fetched
    await dispatch('currentUser/loadCustomerInfo', null, {
      root: true,
    });

    const isLogged = rootState.currentUser.isLogged;

    try {
      if (isLogged === true) {
        const { data } = await clientApi.get(window.route('api.customer.address.index'));

        if (!data.status) {
          return dispatch('showDefaultError');
        }

        const defaultAddress = find(
          data.data.addresses,
          (address) => address.is_delivery === true && address.default === true,
        );

        const selectedAddress = find(
          data.data.addresses,
          (address) => address.id === getters.selectedDeliveryAddressId,
        );

        if (defaultAddress && !selectedAddress && state.cart.delivery_type !== 'BTQP') {
          dispatch('setSelectedDeliveryAddress', defaultAddress);
        }

        await commit(types.CHECKOUT_LOAD_ADDRESSES_SUCCESS, data.data.addresses);

        if (
          !defaultAddress &&
          !selectedAddress &&
          getters.deliveryAddresses.length === 1 &&
          state.cart.delivery_type !== 'BTQP'
        ) {
          dispatch('setSelectedDeliveryAddress', getters.deliveryAddresses[0]);
        }
      } else {
        await commit(
          types.CHECKOUT_LOAD_ADDRESSES_SUCCESS,
          size(get(state, ['cart', 'order', 'delivery_address'])) > 0
            ? [get(state, ['cart', 'order', 'delivery_address'])]
            : [],
        );
      }

      if (getters.selectedDeliveryAddressId === null && state.selectedBoutiqueId === null) {
        dispatch('openAddressForm', { type: 'delivery' });
      }
    } catch (error) {
      if (error.request.status === 403) {
        return commit(types.CHECKOUT_LOAD_ADDRESSES_SUCCESS, {});
      }
      commit(types.CHECKOUT_ERROR, error);
    }
  },

  async fetchBoutiques({ commit, dispatch }) {
    commit(types.CHECKOUT_DELIVERY_LOADING, true);
    try {
      const { data } = await clientApi.get(window.route('api.cart.delivery.boutiques.index'));

      if (data.status) {
        commit(types.CHECKOUT_LOAD_BOUTIQUES_SUCCESS, data.data.boutiques);
        if (data.data.boutiques.length === 0) {
          dispatch('setDeliveryMode', 'home');
        }
      } else {
        dispatch('showDefaultError');
      }
    } catch (error) {
      commit(types.CHECKOUT_ERROR, error);
    }
    commit(types.CHECKOUT_DELIVERY_LOADING, false);
  },

  async removeProduct({ commit, dispatch }, product) {
    try {
      const { data } = await clientApi.delete(
        window.route('api.cart.reservations.product.destroy', {
          id: product.id,
        }),
      );

      if (!data.status) {
        return dispatch('showDefaultError');
      }

      Bag.saveShoppingBagTotalFromResponse(data.data);
      window.tracking.pushEvent(window.tracking.getRemoveFromCartTracking(product.tracking));
      dispatch('cartLoaded', data.data.cart);
    } catch (error) {
      commit(types.CHECKOUT_ERROR, error);
    }
  },

  async setCurrentCartItemId({ commit }, id) {
    commit(types.CHECKOUT_SET_CURRENT_CART_ITEM_ID, id);
  },

  async removeGiftMessage({ commit, dispatch }) {
    try {
      const { data } = await clientApi.delete(window.route('api.cart.gift.delete'));

      if (data.status) {
        dispatch('cartLoaded', data.data.cart);
      } else {
        dispatch('showDefaultError');
      }
    } catch (error) {
      commit(types.CHECKOUT_ERROR, error);
    }
  },

  async addEngravingMessage({ commit, dispatch }, { reservationNumber, message, font }) {
    commit(types.CHECKOUT_DELIVERY_LOADING, true);
    try {
      const { data } = await clientApi.post(
        window.route('api.cart.reservations.engraving.message.store', { reservationNumber }),
        {
          message,
          font,
        },
      );

      if (!data?.status) {
        return dispatch('showDefaultError');
      }

      dispatch('cartLoaded', data.data.cart);
    } catch (error) {
      commit(types.CHECKOUT_ERROR, error);
    } finally {
      commit(types.CHECKOUT_DELIVERY_LOADING, false);
    }
  },

  async removeEngravingMessage({ commit, dispatch }, id) {
    commit(types.CHECKOUT_DELIVERY_LOADING, true);
    try {
      const { data } = await clientApi.delete(
        window.route('api.cart.reservations.engraving.message.destroy', {
          id,
        }),
      );

      dispatch('cartLoaded', data.data.cart);
    } catch (error) {
      commit(types.CHECKOUT_ERROR, error);
    } finally {
      commit(types.CHECKOUT_DELIVERY_LOADING, false);
    }
  },

  showDefaultError({ commit }) {
    commit(types.CHECKOUT_ERROR, new Error(window.Phoenix.i18n.t('Form.validations.default')));
  },

  async acceptSuggestion({ state, commit, dispatch, getters }, address) {
    const {
      data: {
        data: { address: acceptedAddress },
      },
    } = await clientApi.post(window.route('api.customer.address.store'), address);
    commit(types.CHECKOUT_SUGGESTED_ADDRESSES_ACCEPT_SUCCESS, acceptedAddress);
    await dispatch('setSelectedAddress', acceptedAddress);
    if (!getters.hasInlineAddressForm) {
      dispatch('customerAddressForm/closeFormModal', state.checkoutAddressFormModalId, {
        root: true,
      });
    }
  },

  declineSuggestion({ commit }) {
    commit(`customerAddressForm/${types.CUSTOMER_ADDRESS_SUGGESTED_ADDRESSES_DELETE}`, null, {
      root: true,
    });
  },

  async onAddressSavedSuccess({ state, commit, dispatch, getters }, response) {
    const address = response.data.data.address;

    if (!getters.hasInlineAddressForm) {
      dispatch('customerAddressForm/closeFormModal', state.checkoutAddressFormModalId, {
        root: true,
      });
    }

    await dispatch('setSelectedAddress', address);
    commit(types.CHECKOUT_SUGGESTED_ADDRESSES_ACCEPT_SUCCESS, address);
  },

  async setSelectedDeliveryAddress({ state, dispatch, commit, getters }, address) {
    await dispatch('saveDeliveryAddress', {
      data: {
        mode: 'home',
        address,
      },
      successCallback: () => {
        commit(`form/${types.FORM_ERROR}`, null, {
          root: true,
        });
        commit(types.CHECKOUT_SET_SELECTED_DELIVERY_ADDRESS_ID, address.id);
        dispatch('cancelEditAddress');
      },
      errorCallback: (error) => {
        dispatch('customerAddressForm/setAddressIsSaving', false, {
          root: true,
        });

        commit(`form/${types.FORM_ERROR}`, error.response.data.message, {
          root: true,
        });

        // I've added a timeout otherwise the modal is not closed and therefore, the modal does not open again
        setTimeout(() => {
          dispatch('openAddressForm', {
            address,
            modalId: getters.hasInlineAddressForm ? null : state.checkoutAddressFormModalId,
            type: 'delivery',
          });

          dispatch('setStepByName', 'DELIVERY');
        }, 200);
      },
    });
  },

  async setSelectedAddress({ state, dispatch }, address) {
    const setSelectedAddressMethod =
      state.addressType === 'billing' ? 'setSelectedBillingAddress' : 'setSelectedDeliveryAddress';

    await dispatch(setSelectedAddressMethod, address);
  },

  async setSelectedBillingAddress({ dispatch }, address) {
    await dispatch('saveBillingAddress', address);
  },

  async setSelectedBoutique({ state, dispatch, commit }, boutiqueId) {
    if (state.selectedBoutiqueId === boutiqueId && state.cart.delivery_mode === 'pickup') {
      return;
    }

    await dispatch('saveDeliveryAddress', {
      data: {
        mode: 'pickup',
        boutiqueCode: boutiqueId,
      },
      successCallback: () => {
        commit(types.CHECKOUT_SET_SELECTED_BOUTIQUE_ID, boutiqueId);
      },
      errorCallback: (error) => {
        commit(types.CHECKOUT_ERROR, error);
      },
    });
  },

  async saveDeliveryAddress({ commit, dispatch }, { data, successCallback, errorCallback }) {
    commit(types.CHECKOUT_SUMMARY_LOADING, true);
    commit(types.CHECKOUT_DELIVERY_LOADING, true);
    try {
      const response = await clientApi.post(window.route('api.cart.delivery.address.store'), data);

      dispatch('cartLoaded', response.data.data.cart);
      if (successCallback !== undefined) {
        successCallback();
      }
    } catch (error) {
      const errors = error.response.data.errors;

      if (size(errors) === 1 && ['tax', 'reservation'].includes(head(keys(errors)))) {
        return commit(types.CHECKOUT_ERROR, new Error(window.Phoenix.i18n.t(head(errors.tax))));
      }

      if (errorCallback !== undefined) {
        errorCallback(error);
      }

      if (error.response.status !== 422) {
        window.Phoenix.sentry.captureException(error);
      }
    }

    commit(types.CHECKOUT_SUMMARY_LOADING, false);
    commit(types.CHECKOUT_DELIVERY_LOADING, false);
  },

  setBillingIsLoading({ commit }, isLoading) {
    commit(types.CHECKOUT_BILLING_LOADING, isLoading);
  },

  async saveBillingAddress({ state, commit, dispatch, getters }, address) {
    commit(types.CHECKOUT_BILLING_LOADING, true);
    try {
      const response = await clientApi.post(window.route('api.cart.billing.address.store'), { address });
      dispatch('cartLoaded', response.data.data.cart);
      dispatch('cancelEditAddress');
    } catch (error) {
      commit(`form/${types.FORM_ERROR}`, error.response.data.message, {
        root: true,
      });
      dispatch('openAddressForm', {
        address,
        modalId: getters.hasInlineAddressForm ? null : state.checkoutAddressFormModalId,
        type: 'billing',
      });
    }
    commit(types.CHECKOUT_BILLING_LOADING, false);
  },

  async setDeliveryMode({ state, dispatch, commit, getters }, deliveryMode) {
    if (state.cart.delivery_mode === deliveryMode) {
      return;
    }

    switch (deliveryMode) {
      case 'home':
        if (state.selectedDeliveryAddressId === null) {
          commit(types.CHECKOUT_SET_DEFAULT_DELIVERY_TYPE, deliveryMode);
          dispatch('setStepByName', 'DELIVERY');

          if (getters.selectedDeliveryAddressId === null) {
            dispatch('openAddressForm', { type: 'delivery' });
          }

          return;
        }
        await dispatch('setSelectedDeliveryAddress', state.addresses[state.selectedDeliveryAddressId]);
        break;

      case 'pickup':
        if (state.selectedBoutiqueId === null) {
          if (state.boutiques.length === 1) {
            dispatch('setSelectedBoutique', state.boutiques[0].swseBoutiqueCode);
            return;
          }

          commit(types.CHECKOUT_SET_DEFAULT_DELIVERY_TYPE, deliveryMode);
          dispatch('setStepByName', 'DELIVERY');
          return;
        }
        await dispatch('setSelectedBoutique', state.selectedBoutiqueId);
        break;

      default:
        dispatch('showDefaultError');
        break;
    }
  },

  async setDeliveryType({ commit, dispatch }, { deliveryType, date = null, timeRange = null }) {
    commit(types.CHECKOUT_SUMMARY_LOADING, true);
    commit(types.CHECKOUT_DELIVERY_LOADING, true);
    try {
      const response = await clientApi.post(window.route('api.cart.delivery.type.store'), {
        deliveryType,
        date,
        timeRange,
      });
      await dispatch('cartLoaded', response.data.data.cart);
      commit(types.CHECKOUT_SUMMARY_LOADING, false);
      commit(types.CHECKOUT_DELIVERY_LOADING, false);
    } catch (error) {
      commit(types.CHECKOUT_ERROR, error);
    }
  },

  async cartLoaded({ commit, getters, rootState, dispatch }, cart) {
    commit(types.CHECKOUT_LOAD_CART_SUCCESS, cart);

    if (cart.delivery_mode === 'pickup') {
      commit(types.CHECKOUT_SET_SELECTED_BOUTIQUE_ID, cart.order.delivery_address.deliveryBoutiqueCode);
    }

    if (
      !rootState.currentUser.isLogged &&
      getters.selectedBillingAddressId === null &&
      (cart.delivery_mode === 'pickup' || getters.selectedDeliveryAddressId !== null)
    ) {
      dispatch('openAddressForm', { type: 'billing' });
    }

    if (!getters.allProductsHaveStock) {
      window.Phoenix.toaster.notify({
        type: 'danger',
        text: window.Phoenix.i18n.t('Product.someHasNoStock'),
      });

      window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    if (!getters.isDeliverable) {
      window.Phoenix.toaster.notify({
        type: 'danger',
        text: window.Phoenix.i18n.t('Product.someAreNotDeliverable'),
      });

      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  },

  openAddressForm({ commit, dispatch, getters }, { modalId = null, address = null, type }) {
    commit(types.CHECKOUT_SET_ADDRESS_TYPE, type);
    if (address === null) {
      if (getters.hasInlineAddressForm) {
        commit(types.CHECKOUT_SET_SELECTED_ADDRESS_ID, { id: 0, type });
      }
      commit(types.CHECKOUT_SET_EDITING_ADDRESS_ID, 0);
    } else {
      commit(types.CHECKOUT_SET_EDITING_ADDRESS_ID, address.id);
    }
    dispatch('customerAddressForm/openForm', { modalId, address }, { root: true });
  },

  async setUseSameAsDelivery({ state, commit, dispatch, getters, rootState }, checked) {
    commit(types.CHECKOUT_BILLING_LOADING, true);
    try {
      const response = await clientApi.post(window.route('api.cart.billing.address.sameAsDelivery'), {
        sameAsDelivery: checked,
      });

      await dispatch('cartLoaded', response.data.data.cart);

      if (
        !rootState.currentUser.isLogged &&
        !state.cart.order.billing_same_as_delivery &&
        getters.selectedBillingAddressId === null
      ) {
        dispatch('openAddressForm', { type: 'billing' });
      }
    } catch (error) {
      commit(types.CHECKOUT_ERROR, error);
    }
    commit(types.CHECKOUT_BILLING_LOADING, false);
  },

  async proceedPayment({ commit, getters }, payment) {
    try {
      const { data } = await clientApi.post(window.route('api.cart.payment.proceed', { type: payment.type }), payment);
      window.tracking.addCheckoutStepEvent(
        'payment',
        window.tracking.getCheckoutStep4Tracking(
          getters.tracking.items,
          getters.tracking.currency,
          getters.tracking.userstatus,
          payment.type,
        ),
      );
      if (data.data.threeDSecure) {
        commit(types.CHECKOUT_SET_3DSECURE, data.data.threeDSecure);
      } else if (data.data.threeDSecure2IdentifyShopper) {
        commit(types.CHECKOUT_SET_3DSECURE2_IDENTIFY_SHOPPER, data.data.threeDSecure2IdentifyShopper);
      } else if (data.data.threeDSecure2ChallengeShopper) {
        commit(types.CHECKOUT_SET_3DSECURE2_CHALLENGE_SHOPPER, data.data.threeDSecure2ChallengeShopper);
      } else {
        window.location.href = window.route('order_confirmation_page');
      }
      return true;
    } catch (error) {
      commit(types.CHECKOUT_ERROR, error);
      return false;
    }
  },

  async proceedAdyenPayment({}, status) {
    try {
      await clientApi.get(window.route('api.cart.payment.proceed.adyen-rna', { adyenReturnResponse: status }));
      window.tracking.addCheckoutStepEvent(
        'payment',
        window.tracking.getCheckoutStep4Tracking(
          getters.tracking.items,
          getters.tracking.currency,
          getters.tracking.userstatus,
          'ADYEN',
        ),
      );
      window.location.href = window.route('order_confirmation_page');
    } catch (error) {
      window.location.href = window.route('checkout_page');
    }
  },

  async saveInvoiceSent({ commit, dispatch }, invoiceSent) {
    commit(types.CHECKOUT_PAYMENT_LOADING, true);
    try {
      const { data } = await clientApi.post(window.route('api.cart.invoiceSent.store'), { invoiceSent });
      dispatch('cartLoaded', data.data.cart);
    } catch (error) {
      commit(types.CHECKOUT_ERROR, error);
    }
    commit(types.CHECKOUT_PAYMENT_LOADING, false);
  },

  async getPaymentForm({ commit }, paymentType) {
    try {
      const { data } = await clientApi.get(window.route('api.cart.payment.form.show', { type: paymentType }));
      commit(types.CHECKOUT_SET_PAYMENT_FORM, {
        paymentType,
        form: data.data.form,
      });
    } catch (error) {
      commit(types.CHECKOUT_ERROR, error);
    }
  },

  async saveFapiaoType({ commit, state, dispatch }, type) {
    if (type === state.cart.fapiao_type) {
      return;
    }

    commit(types.CHECKOUT_BILLING_LOADING, true);
    try {
      const { data } = await clientApi.post(window.route('api.cart.billing.fapiao.type.store'), { type });

      if (data.status) {
        dispatch('cartLoaded', data.data.cart);
      } else {
        dispatch('showDefaultError');
      }
    } catch (error) {
      commit(types.CHECKOUT_ERROR, error);
    }
    commit(types.CHECKOUT_BILLING_LOADING, false);
  },

  async saveContactInformation({ commit, dispatch, getters }, formData) {
    commit(types.CHECKOUT_BILLING_LOADING, true);
    try {
      const { data } = await clientApi.post(window.route('api.cart.billing.contactInformation.store'), formData);

      if (data.status) {
        if (getters.countryCode === 'KR') {
          const url = new URL(window.location.href);
          url.searchParams.set('step', data.data.cart.step);
          window.location.href = url;
          return;
        }

        if (formData.password) {
          await dispatch('reloadCustomerData');

          window.Phoenix.toaster.notify({
            type: 'success',
            text: window.Phoenix.i18n.t('MyAccount.AccountCreateSuccessMessage'),
          });
        }
        dispatch('cartLoaded', data.data.cart);
      } else {
        dispatch('showDefaultError');
      }
    } catch (error) {
      commit(types.CHECKOUT_ERROR, error);
      commit(types.CHECKOUT_BILLING_LOADING, false);
      return error.response.data;
    }
    commit(types.CHECKOUT_BILLING_LOADING, false);
  },

  setStepByName({ commit, getters }, name) {
    commit(types.CHECKOUT_SET_STEP, getters.stepByName(name));
  },

  clearError({ commit }) {
    commit(types.CHECKOUT_ERROR, null);
  },

  set3DSecure({ commit }, threeDSecure) {
    commit(types.CHECKOUT_SET_3DSECURE, threeDSecure);
  },

  async saveCreditCard({ commit, dispatch }, creditCardData) {
    try {
      const { data } = await clientApi.post(window.route('api.cart.payment.saveCreditCard'), creditCardData);

      if (data.status) {
        return true;
      } else {
        dispatch('showDefaultError');
      }
    } catch (error) {
      commit(types.CHECKOUT_ERROR, error);
    }
    return false;
  },

  async saveCreditCardInstallment({ commit, dispatch }, ccInstallment) {
    try {
      const { data } = await clientApi.post(window.route('api.cart.payment.saveCreditCardInstallment'), {
        ccInstallment,
      });

      if (data.status) {
        return true;
      } else {
        dispatch('showDefaultError');
      }
    } catch (error) {
      commit(types.CHECKOUT_ERROR, error);
    }
    return false;
  },

  async reloadCustomerData({ dispatch }) {
    await dispatch('currentUser/loadCustomerInfo', true, {
      root: true,
    });
    await dispatch('fetchAddresses');
  },

  async sendNotificationResult({ commit }, result) {
    try {
      const { data } = await clientApi.post(window.route('checkout.adyen.3ds2.notification.result'), result);
      if (data.data.threeDSecure2ChallengeShopper) {
        commit(types.CHECKOUT_SET_3DSECURE2_CHALLENGE_SHOPPER, data.data.threeDSecure2ChallengeShopper);
        return;
      }

      window.location.href = window.route('order_confirmation_page');
    } catch (error) {
      commit(types.CHECKOUT_ERROR, error);
      throw new Error();
    }
  },

  async send3DS2Result({ commit }, result) {
    try {
      await clientApi.post(window.route('checkout.adyen.3ds2.result'), result);
      window.location.href = window.route('order_confirmation_page');
    } catch (error) {
      commit(types.CHECKOUT_ERROR, error);
      return false;
    }

    return true;
  },

  async setGiftWrapping({ commit, dispatch }, hasWrapping) {
    try {
      const { data } = await clientApi.put(window.route('api.cart.gift.wrapping.update'), { hasWrapping });
      if (data.status) {
        dispatch('cartLoaded', data.data.cart);
      } else {
        dispatch('showDefaultError');
      }
    } catch (error) {
      commit(types.CHECKOUT_ERROR, error);
    }
  },

  setGroupsAndCountries({ commit }, { groups, countries }) {
    commit(types.CHECKOUT_SET_GROUPS_AND_COUNTRIES, { groups, countries });
  },

  cancelEditAddress({ commit }) {
    commit(types.CHECKOUT_SET_EDITING_ADDRESS_ID, null);
  },

  addMountedStep({ commit, getters }, step) {
    commit(types.CHECKOUT_ADD_MOUNTED_STEP, getters.stepByName(step));
  },

  storeGuestEmailAddress({ commit }, email) {
    commit(types.CHECKOUT_SET_GUEST_EMAIL_ADDRESS, email);
  },
};

const mutations = {
  [types.CHECKOUT_SET_GROUPS_AND_COUNTRIES](state, { groups, countries }) {
    state.countries = countries;
    state.groups = groups;
  },

  [types.CHECKOUT_SET_LOADING](state, isLoading) {
    state.isLoading = isLoading;
  },

  [types.CHECKOUT_LOAD_CART_SUCCESS](state, cart) {
    state.cart = cart;
    state.isLoading = false;
  },

  /**
   * handle error in checkout
   * @param state
   * @param error can be an axios error or an Error object
   */
  [types.CHECKOUT_ERROR](state, error) {
    if (error) {
      window.Phoenix.sentry.captureException(error);
      state.error = get(error, 'response.data.message', error.message);
    } else {
      state.error = null;
    }

    state.isLoading = false;
  },

  [types.CHECKOUT_SET_CURRENT_CART_ITEM_ID](state, id) {
    state.currentCartItemId = id;
  },

  [types.CHECKOUT_SUGGESTED_ADDRESSES_ACCEPT_SUCCESS](state, address) {
    const id = address.id === undefined ? 1 : address.id;
    set(state, ['addresses', id], address);
  },

  [types.CHECKOUT_LOAD_ADDRESSES_SUCCESS](state, addresses) {
    const deliveryAddresses = {};
    if (addresses && addresses.length > 0) {
      forEach(addresses, (address) => {
        deliveryAddresses[address.id] = address;
      });
    }
    state.addresses = deliveryAddresses;
  },

  [types.CHECKOUT_SUMMARY_LOADING](state, isLoading) {
    state.summaryLoading = isLoading;
  },

  [types.CHECKOUT_DELIVERY_LOADING](state, isLoading) {
    state.deliveryLoading = isLoading;
  },

  [types.CHECKOUT_LOAD_BOUTIQUES_SUCCESS](state, boutiques) {
    if (state.cart.delivery_boutique_code) {
      const selectedBoutique = find(boutiques, (b) => b.swseBoutiqueCode === state.cart.delivery_boutique_code);
      if (selectedBoutique) {
        state.selectedBoutiqueId = selectedBoutique.swseBoutiqueCode;
      }
    }
    state.boutiques = boutiques;
  },

  [types.CHECKOUT_SET_SELECTED_BOUTIQUE_ID](state, boutiqueId) {
    state.selectedBoutiqueId = boutiqueId;
  },

  [types.CHECKOUT_SET_SELECTED_ADDRESS_ID](state, { id, type }) {
    set(state, ['cart', 'order', `selected_${type}_address_id`], id);
  },

  [types.CHECKOUT_SET_SELECTED_DELIVERY_ADDRESS_ID](state, id) {
    state.selectedDeliveryAddressId = id;
  },

  [types.CHECKOUT_BILLING_LOADING](state, loading) {
    state.billingLoading = loading;
  },

  [types.CHECKOUT_PAYMENT_LOADING](state, loading) {
    state.paymentLoading = loading;
  },

  [types.CHECKOUT_SET_ADDRESS_TYPE](state, type) {
    state.addressType = type;
  },

  [types.CHECKOUT_SET_DEFAULT_DELIVERY_TYPE](state, type) {
    set(state, ['cart', 'delivery_mode'], type);
    set(state, ['cart', 'delivery_type'], '');
    if (type === 'pickup') {
      set(state, ['cart', 'delivery_boutique_code'], '');
    }
  },

  [types.CHECKOUT_SET_FAPIAO_TYPE](state, type) {
    set(state, ['cart', 'fapiao_type'], type);
  },

  [types.CHECKOUT_SET_3DSECURE](state, threeDSecure) {
    state.threeDSecure = threeDSecure;
  },

  [types.CHECKOUT_SET_3DSECURE2_IDENTIFY_SHOPPER](state, threeDSecure2IdentifyShopper) {
    state.threeDSecure2IdentifyShopper = threeDSecure2IdentifyShopper;
  },

  [types.CHECKOUT_SET_3DSECURE2_CHALLENGE_SHOPPER](state, threeDSecure2ChallengeShopper) {
    state.threeDSecure2ChallengeShopper = threeDSecure2ChallengeShopper;
  },

  [types.CHECKOUT_SET_PAYMENT_FORM](state, { paymentType, form }) {
    if (has(state, ['cart', 'payment_types', paymentType]) === true) {
      set(state, ['cart', 'payment_types', paymentType, 'form'], form);
    }
  },

  [types.CHECKOUT_SET_STEP](state, step) {
    state.cart.step = step;
  },

  [types.CHECKOUT_SET_EDITING_ADDRESS_ID](state, addressId) {
    state.editingAddressId = addressId;
  },

  [types.CHECKOUT_ADD_MOUNTED_STEP](state, step) {
    state.mountedSteps.push(step);
  },

  [types.CHECKOUT_SET_GUEST_EMAIL_ADDRESS](state, email) {
    state.guestEmailAddress = email;
  },
};

export default {
  namespaced: true,
  state: initialState,
  getters,
  actions,
  mutations,
};
